import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { task } from 'ember-concurrency'
import perform from 'ember-concurrency/helpers/perform'
import type Assignment from 'teacher-ui-client/models/assignment'
import type ReadAloudReview from 'teacher-ui-client/models/read-aloud-review'
import {
  ThemedModal,
  CloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'teacher-ui-client/components/base/themed-modal'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { eq, not, or } from 'ember-truth-helpers'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { TextArea } from 'teacher-ui-client/components/base/text-area'
import { Collapser } from 'teacher-ui-client/components/collapser'
import { service } from '@ember/service'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import { t, type IntlService } from 'ember-intl'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { toRequestScopeStudent, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { LinkTo } from '@ember/routing'
import { hash } from '@ember/helper'
import type Store from '@ember-data/store'
import { hasChapters } from 'teacher-ui-client/models/assignment'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface Signature {
  Args: {
    assignment: Assignment
    review: ReadAloudReview
    close: () => unknown
    onSaved?: () => void
  }
  Blocks: {
    default: []
  }
}

const scores = [1, 2, 3, 4, 5]

export interface SubmissionEventReportData extends BaseReport {
  events: { delete_audio_url: string; audio_url: string }[]
  meta: BaseReportMetaPaging
}

export class ReadAloudReviewModal extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service log!: Log

  @service store!: Store

  @service intl!: IntlService

  @service reporting!: ReportingService

  @tracked showDeleteConfirmation: boolean = false

  /**
   * We make a request to the reporting endpoint to receive a student event, for the latest submission event. This will
   * contain the audio url we need.
   */
  fetchAudioUrl = trackedFunction(this, async () => {
    // this modal shouldn't be opened if student for review has been deleted - but check regardless.
    if (!this.args.review.student?.id || !this.args.assignment.id) return

    const requestScope = toRequestScopeStudent(this.args.review.student.id)

    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope,
      reportName: 'read-aloud-assignments/submission-events',
    })

    const query = this.reporting.buildQuery({
      filter: { assignment_uuid: this.args.assignment.id },
      sort: '-created_at', // the latest first
      page: { number: 1, size: 1 },
      period: { start: this.args.assignment.startAt, end: this.args.assignment.endAt },
    })

    const report = await this.reporting.fetchReport<SubmissionEventReportData>(url, query)

    const submissionEvent = report.events[0]
    if (submissionEvent) {
      const deleteAudioUrl = submissionEvent.delete_audio_url
      const audioUrl = submissionEvent.audio_url
      // check the URL with a HEAD request, audio may have been deleted
      if (audioUrl) {
        const response = await fetch(audioUrl, { method: 'HEAD' })
        if (response.ok) {
          return { deleteAudioUrl, audioUrl }
        }
      }
    }
  })

  bookDetails = trackedFunction(this, async () => {
    const code = this.args.assignment.assignmentDetails.assignment_id

    await Promise.resolve() // disconnect tracking before store calls

    const bookQuery = await this.store.query<BookLibraryBook>('book-library/book', { filter: { code } })

    return bookQuery[0]
  })

  setScore = (score: number) => {
    this.args.review.score = score
  }

  /**
   * Number input will provide this function with '' (empty input) or stringint '1', '2', ...
   */
  setWordsPerMinute = (wpm: string) => {
    const wordsPerMinute = parseInt(wpm, 10)
    if (Number.isNaN(wordsPerMinute)) {
      this.args.review.wordsPerMinute = undefined
    } else {
      this.args.review.wordsPerMinute = wordsPerMinute
    }
  }

  setComments = (comments: string) => {
    // if they deleted the comment, rather than saving empty string, just delete it
    this.args.review.comments = comments.length ? comments : undefined
  }

  insertQuickComment = (quickComment: string) => {
    const lastCharacter = this.args.review.comments?.slice(-1)
    if (!lastCharacter) {
      this.args.review.comments = quickComment
    } else if (lastCharacter.match(/\s/)) {
      // last is whitespace, just append
      this.args.review.comments = `${this.args.review.comments}${quickComment}`
    } else if (lastCharacter === ',' || lastCharacter === '.') {
      this.args.review.comments = `${this.args.review.comments} ${quickComment}`
    } else {
      this.args.review.comments = `${this.args.review.comments}, ${quickComment}`
    }
  }

  saveReview = task({ drop: true }, async () => {
    try {
      await this.args.review.save()
      this.args.onSaved?.()
      this.flashQueue.addSuccess({ subtitle: this.intl.t('assignments.reviewModal.saveSuccess') })
      this.args.close()
    } catch (error: unknown) {
      this.log.error('failed to save review', error ?? undefined)
      this.flashQueue.addFail({ subtitle: this.intl.t('assignments.reviewModal.saveFailed') })
    }
  })

  deleteAudio = task({ drop: true }, async (deleteAudioUrl: string) => {
    const response = await fetch(deleteAudioUrl, { method: 'DELETE' })
    if (response.ok) {
      this.flashQueue.addSuccess({ subtitle: this.intl.t('assignments.reviewModal.audioDeleteSuccess') })
      void this.fetchAudioUrl.retry()
      this.showDeleteConfirmation = false
    } else {
      this.flashQueue.addFail({ subtitle: this.intl.t('assignments.reviewModal.audioDeleteFailed') })
      const error = await response.text()
      this.log.error('failed to delete audio', error ?? undefined)
    }
  })

  close = () => {
    this.args.review?.rollbackAttributes()
    this.args.close()
  }

  askDeleteAudio = () => {
    this.showDeleteConfirmation = true
  }

  cancelDeleteAudio = () => {
    this.showDeleteConfirmation = false
  }

  get suggestedQuickComments() {
    const defaultQuickComments = [
      this.intl.t('assignments.reviewModal.quickComments.lotsOfErrors'),
      this.intl.t('assignments.reviewModal.quickComments.readsSlowly'),
      this.intl.t('assignments.reviewModal.quickComments.missedPunctuation'),
      this.intl.t('assignments.reviewModal.quickComments.lacksExpression'),
    ]

    switch (this.args.review.score) {
      case 1:
        return defaultQuickComments
      case 2:
        return [
          this.intl.t('assignments.reviewModal.quickComments.someWordsCorrectly'),
          this.intl.t('assignments.reviewModal.quickComments.irregularPace'),
          this.intl.t('assignments.reviewModal.quickComments.missedMostPunctuation'),
          this.intl.t('assignments.reviewModal.quickComments.littleExpression'),
        ]
      case 3:
        return [
          this.intl.t('assignments.reviewModal.quickComments.fewWordsCorrect'),
          this.intl.t('assignments.reviewModal.quickComments.paceImproving'),
          this.intl.t('assignments.reviewModal.quickComments.somePunctuation'),
          this.intl.t('assignments.reviewModal.quickComments.someExpression'),
        ]
      case 4:
        return [
          this.intl.t('assignments.reviewModal.quickComments.fewErrors'),
          this.intl.t('assignments.reviewModal.quickComments.regularPace'),
          this.intl.t('assignments.reviewModal.quickComments.mostPunctuation'),
          this.intl.t('assignments.reviewModal.quickComments.goodExpression'),
        ]
      case 5:
        return [
          this.intl.t('assignments.reviewModal.quickComments.allWordsCorrect'),
          this.intl.t('assignments.reviewModal.quickComments.wellPaced'),
          this.intl.t('assignments.reviewModal.quickComments.clearAndAccurate'),
          this.intl.t('assignments.reviewModal.quickComments.greatExpression'),
        ]
      default:
        return defaultQuickComments
    }
  }

  get bookChapterFormatted() {
    /*
     * BookLibraryBook's chapters are indexed from 0; chapter 1 would be at index 0
     * However we get assigned chapter from the assignment as a number starting from 1, so chapter 1 is given as "1"
     * Therefore we need to -1 to index BookLibrayBook's chapter array properly
     */

    if (this.args.assignment && hasChapters(this.args.assignment)) {
      const formattedIndex = this.args.assignment.assignedChapters?.[0] - 1

      return this.bookDetails?.value?.bookMetadata?.chapters?.[formattedIndex]?.name ?? ''
    }
  }

  get bookId() {
    return this.bookDetails?.value?.id
  }

  <template>
    <ThemedModal @show={{true}} @noOutsideClose={{true}}>
      <ModalHeader>
        <div>
          <h3>
            {{t "assignments.reviewModal.readAloudFluency"}}
            -
            {{@review.student.fullName}}</h3>
          <LinkTo
            @route="subscription-type.book-library.search"
            @query={{hash bookId=this.bookId}}
            data-test-query-book-id={{this.bookId}}
            target="_blank"
          >
            {{@assignment.assignmentDetails.book_name}}
            <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
          </LinkTo>
          {{#if this.bookChapterFormatted}}
            <div data-test-modal-chapters>
              <span>{{t "assignments.reviewModal.assignedChapters" chapterCount=@assignment.assignedChapters.length}}
              </span>
              <span>{{this.bookChapterFormatted}}</span>
            </div>
          {{/if}}

        </div>
        <CloseButton @close={{this.close}} />
      </ModalHeader>
      <ModalBody class="w-3xl">
        {{! min-height with vertical centering avoids layout shift between states }}
        <div
          data-test-audio-box
          class="flex min-h-32 items-center gap-4 rounded-lg border p-8
            {{if
              (or this.fetchAudioUrl.isPending this.fetchAudioUrl.value)
              'border-oceany-blue-100 bg-oceany-blue-50'
              'border-dusty-black-100 bg-dusty-black-50'
            }}"
        >
          {{#if this.fetchAudioUrl.isPending}}
            <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
          {{else if this.fetchAudioUrl.value}}
            {{#if this.showDeleteConfirmation}}
              <span class="mr-auto">{{t "assignments.reviewModal.audioDeletion" htmlSafe=true}}</span>
              <button
                data-test-delete-audio-cancel
                type="button"
                class="font-semibold {{if this.deleteAudio.isRunning 'text-dusty-black-250' 'text-oceany-blue-350'}}"
                disabled={{this.deleteAudio.isRunning}}
                {{on "click" this.cancelDeleteAudio}}
              >
                {{t "assignments.reviewModal.audioDeleteNo"}}
              </button>
              <button
                data-test-delete-audio-confirm
                type="button"
                class="font-semibold
                  {{if this.deleteAudio.isRunning 'text-dusty-black-250' 'text-watermelony-red-350'}}"
                disabled={{this.deleteAudio.isRunning}}
                {{on "click" (perform this.deleteAudio this.fetchAudioUrl.value.deleteAudioUrl)}}
              >
                {{t "assignments.reviewModal.audioDeleteYes"}}
              </button>
            {{else}}
              {{! template-lint-disable require-media-caption }}
              {{! Cannot caption user-generated audio }}
              <audio
                aria-label={{t "assignments.reviewModal.audioAlt"}}
                class="w-full"
                controls
                src={{this.fetchAudioUrl.value.audioUrl}}
              ></audio>
              <button data-test-delete-audio type="button" class="self-stretch px-6" {{on "click" this.askDeleteAudio}}>
                <PhosphorIcon @icon="phosphor-trash-fill" />
              </button>
            {{/if}}
          {{else}}
            {{t "assignments.reviewModal.audioMissing"}}
          {{/if}}
        </div>
        <div class="mt-6">
          <div class="font-bold">{{t "assignments.reviewModal.score"}}</div>
          <p class="mt-2">{{t "assignments.reviewModal.howWouldYouScore"}}</p>
          <ol class="my-4">
            <li>{{t "assignments.reviewModal.score1" htmlSafe=true}}</li>
            <li>{{t "assignments.reviewModal.score3" htmlSafe=true}}</li>
            <li>{{t "assignments.reviewModal.score5" htmlSafe=true}}</li>
          </ol>
          <div class="grid grid-cols-5 grid-rows-1 gap-2" role="radiogroup" data-test-scores>
            {{#each scores as |score|}}
              <UiButton
                role="radio"
                aria-checked="{{if (eq @review.score score) true false}}"
                data-value={{score}}
                @appearance={{if (eq @review.score score) ButtonAppearance.regular ButtonAppearance.muted}}
                {{on "click" (fn this.setScore score)}}
              >
                {{score}}
              </UiButton>
            {{/each}}
          </div>
          <div class="mt-2 flex justify-between text-sm">
            <span>{{t "assignments.reviewModal.nonFluent"}}</span>
            <span>{{t "assignments.reviewModal.fluentAndAccurate"}}</span>
          </div>
        </div>
        <Collapser @open={{Boolean @review.score}}>
          <div class="mt-6">
            <div class="font-bold">
              {{t "assignments.reviewModal.wordsPerMinute"}}
              <span class="font-normal">{{t "assignments.reviewModal.optional"}}</span>
            </div>
            <InputText
              data-test-wpm
              id="words-per-minute"
              name="words-per-minute"
              class="mt-2"
              type="number"
              min="0"
              value={{@review.wordsPerMinute}}
              placeholder={{t "assignments.reviewModal.wordsPerMinutePlaceholder"}}
              @themeKey={{ThemeKeys.blue}}
              {{on "input" (eventValue this.setWordsPerMinute)}}
            />
          </div>
          <div class="mt-6">
            <div class="font-bold">
              {{t "assignments.reviewModal.comments"}}
              <span class="font-normal">{{t "assignments.reviewModal.optional"}}</span>
            </div>
            <TextArea
              data-test-comments
              class="mt-2 w-full"
              rows="3"
              placeholder={{t "assignments.reviewModal.commentsPlaceholder"}}
              @value={{@review.comments}}
              @themeKey={{ThemeKeys.blue}}
              {{on "input" (eventValue this.setComments)}}
            />
            <ul class="mt-2">
              {{#each this.suggestedQuickComments as |quickComment|}}
                <li class="inline-block">
                  <button
                    data-test-quick-comment={{quickComment}}
                    type="button"
                    class="border-dusty-black-100 hover:bg-dusty-black-50 cursor-pointer rounded-3xl border px-3 py-1 text-sm whitespace-nowrap"
                    {{on "click" (fn this.insertQuickComment quickComment)}}
                  >
                    {{quickComment}}
                  </button>
                </li>
              {{/each}}
            </ul>
          </div>
        </Collapser>
      </ModalBody>
      <ModalFooter class="space-x-2 text-right">
        <UiButton data-test-cancel @appearance={{ButtonAppearance.muted}} {{on "click" this.close}}>
          {{t "cancel"}}
        </UiButton>
        <UiButton
          data-test-save
          @appearance={{ButtonAppearance.regular}}
          @disabled={{or (not @review.hasDirtyAttributes) (not @review.score)}}
          {{on "click" (perform this.saveReview)}}
        >
          {{t "save"}}
        </UiButton>
      </ModalFooter>
    </ThemedModal>
  </template>
}
